import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from '@zola/zola-ui/src/components/Link';
import TileSwatchList from '../../components/common/shop/ProductTile/TileSwatchList';
import Attributes, { buildSkuAttributeMap, buildLookAttributeMap } from './Attributes';
import {
  renderPersonalizationAction,
  renderActionButton,
  renderQuantity,
  updateProductLook,
  MSRPToolTip,
  handleAttributeChange,
} from './ProductOverviewCore';
import { findSwatchByProductLookId, getProductPrices, formatPrice } from './helpers';
import featureFlags from '../../util/featureFlags';

const ProductOverview = (props) => {
  const {
    productData,
    activeProductLook,
    setActiveProductLook,
    productAttributes,
    setProductAttributes,
    quantity,
    setQuantity,
    setActiveSku,
    activeSku,
    handleAddToRegistry,
    onPdpRedirect,
    handleAddToCart,
  } = props;
  if (!productData || !activeProductLook) return null;
  const [skuAttributeMap] = useState(buildSkuAttributeMap(productData.product_look_views));
  const [lookAttributeMap] = useState(buildLookAttributeMap(productData.attributes));
  const productName = productData.display_name;
  const activeSwatch = findSwatchByProductLookId(activeProductLook.id, productData.swatches);
  const productPrices = getProductPrices(
    activeProductLook,
    productAttributes,
    productData,
    activeSku
  );
  const { actions } = !activeSku ? activeProductLook : activeSku;
  const actionHandlers = {
    ADD_TO_REGISTRY: handleAddToRegistry,
    ADD_TO_CART: handleAddToCart,
  };

  const setters = {
    setActiveProductLook,
    setProductAttributes,
    setActiveSku,
    isKitProduct: true,
  };

  const handlePdpRedirect = () => {
    const openPersonalizationModal = true;
    onPdpRedirect(openPersonalizationModal);
  };

  const onSwatchChange = (productLookId) => {
    updateProductLook(productLookId, productData, setters);
  };

  const onAttributeChange = handleAttributeChange(
    productData,
    productAttributes,
    activeProductLook,
    setters
  );
  const mustBePersonalized =
    featureFlags.get('personalization') && activeSku && activeSku.must_be_personalized;

  return (
    <div className="product-overview-row_container">
      <div className="product-details_container">
        <div className="product-details">
          <div className="brand-product-names-nonmobile">
            <Link
              noUnderline
              onClick={onPdpRedirect}
              href={`/shop/product/${activeProductLook.key}`}
              className="product-name"
            >
              {productName}
            </Link>
          </div>
          {productPrices.msrp ? (
            <div className="msrp-price">
              MSRP
              <span data-tooltip={MSRPToolTip}>
                <i className="zolaicon-info" />
              </span>{' '}
              {productPrices.msrp}
            </div>
          ) : null}
          {productPrices.sale ? (
            <div className="sale-price">
              Was&nbsp;<span>{productPrices.sale}</span>
            </div>
          ) : null}
          <div className="zola-price">{productPrices.zolaPrice}</div>
          <span className="price-matching-text">
            <Link
              variant="primary"
              noUnderline
              href="/wedding-registry/benefits/price-match"
              className="base-text"
            >
              Price Matching Available
            </Link>
          </span>
          {activeSwatch ? (
            <div className="active-color">
              <span>{productData.swatch_group_label}:</span> {activeSwatch.label}
            </div>
          ) : null}
          <TileSwatchList
            activeProductLookId={activeProductLook.id}
            limit={99}
            swatches={productData.swatches}
            onSwatchChange={onSwatchChange}
          />
          <Attributes
            productData={productData}
            activeProductLook={activeProductLook}
            productAttributes={productAttributes}
            setProductAttributes={setProductAttributes}
            skuAttributeMap={skuAttributeMap}
            lookAttributeMap={lookAttributeMap}
            onAttributeChange={onAttributeChange}
          />
        </div>
        <div className="user-input-form">
          {renderQuantity(productData, quantity, setQuantity)}
          <div className="action-buttons">
            {mustBePersonalized
              ? renderPersonalizationAction(handlePdpRedirect)
              : actions.map((action, i) => (
                  <Fragment key={i}>
                    {renderActionButton(action, actionHandlers[action.key])}
                  </Fragment>
                ))}
          </div>
          {activeSku && activeSku.stock_message && (
            <div className="stock-message_container">
              <div className="stock-message-text">{activeSku.stock_message}</div>
            </div>
          )}
          {activeSku && activeSku.free_shipping ? (
            <Link variant="secondary" href="/shipping" className="free-shipping-text">
              <span className="fast-shipping-icon" />
              Free Shipping & Returns
            </Link>
          ) : null}
          {!!activeSku?.delivery_surcharge_cents && (
            <Link variant="secondary" href="/shipping" className="free-shipping-text">
              Delivery Surcharge: {formatPrice(activeSku.delivery_surcharge_cents)}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

ProductOverview.propTypes = {
  productData: PropTypes.shape({
    product_look_views: PropTypes.shape({}),
    attributes: PropTypes.shape({}),
    display_name: PropTypes.string,
    swatch_group_label: PropTypes.string,
    swatches: PropTypes.arrayOf(
      PropTypes.shape({
        product_look_id: PropTypes.string,
      })
    ),
  }), // fetchProductByKey payload
  activeProductLook: PropTypes.shape({
    id: PropTypes.string,
    key: PropTypes.string,
  }), // fetchProductByKey payload > product_look_views
  setActiveProductLook: PropTypes.func.isRequired,
  productAttributes: PropTypes.shape({}),
  setProductAttributes: PropTypes.func.isRequired,
  quantity: PropTypes.number,
  setActiveSku: PropTypes.func.isRequired,
  activeSku: PropTypes.shape({
    must_be_personalized: PropTypes.bool,
    stock_message: PropTypes.string,
    free_shipping: PropTypes.string,
    delivery_surcharge_cents: PropTypes.number,
  }), // fetchProductByKey payload > sku_previews
  handleAddToCart: PropTypes.func.isRequired,
  handleAddToRegistry: PropTypes.func.isRequired,
  setQuantity: PropTypes.func.isRequired,
  onPdpRedirect: PropTypes.func,
};

export default ProductOverview;
